/**
 * Cursor pagination of the list requests (design D9): `{ limit?, cursor? }` -> `{ items, next }`.
 *
 * A cursor is opaque to clients. Here it is `<kind>.<part>.<part>...` (CURSOR_PATTERN characters only): the list
 * kind plus the sort key of the last item of the page (keyset pagination: an item deleted or added in between does
 * not shift the following pages), or an offset for ranked lists that are paged by position. A cursor of another
 * list, a malformed one, or one whose parts do not parse is BAD_REQUEST { reason: 'bad_cursor' }.
 */
import { BackendError, PROTOCOL_ERROR_REASONS } from '../../shared/protocol/errors';
import type { ListPage } from '../../shared/protocol/messages';

const PART = /^[A-Za-z0-9_-]{1,64}$/;

export function badCursor(what = 'list'): BackendError {
  return new BackendError('BAD_REQUEST', `Unknown ${what} cursor`, { reason: PROTOCOL_ERROR_REASONS.badCursor });
}

/** `limit` bounded to 1..max (default when absent). */
export function pageLimit(limit: number | undefined, def: number, max: number): number {
  return Math.max(1, Math.min(max, limit ?? def));
}

export function encodeCursor(kind: string, parts: readonly (string | number)[]): string {
  const text = [kind, ...parts.map(String)];
  for (const p of text) if (!PART.test(p)) throw new Error(`cursor part "${p}" is not encodable`);
  return text.join('.');
}

/** The parts of a cursor of `kind` with exactly `arity` parts; throws bad_cursor otherwise. */
export function decodeCursor(cursor: string, kind: string, arity: number): string[] {
  const text = cursor.split('.');
  if (text.length !== arity + 1 || text[0] !== kind || !text.every((p) => PART.test(p))) throw badCursor();
  return text.slice(1);
}

/** A cursor part that must be a safe non-negative integer (or throws bad_cursor). */
export function cursorInt(part: string): number {
  const n = /^\d{1,16}$/.test(part) ? Number(part) : NaN;
  if (!Number.isSafeInteger(n)) throw badCursor();
  return n;
}

/**
 * One page of an already sorted list: the first `limit` items after the cursor position (`after(item)` says
 * whether an item comes after it; null = the first page), `next` = the cursor of the page's last item when more
 * items follow.
 */
export function keysetPage<T>(sorted: readonly T[], limit: number, after: ((item: T) => boolean) | null, cursorOf: (item: T) => string): ListPage<T> {
  const start = after ? sorted.findIndex(after) : 0;
  const rest = start < 0 ? [] : sorted.slice(start);
  const items = rest.slice(0, limit);
  return { items, next: rest.length > limit ? cursorOf(items[items.length - 1]) : null };
}
